










































import { Vue, Prop, Component } from 'vue-property-decorator';
import {
    ProductSeriesDropDownValueViewObject,
    ProductSeriesDropDownViewObject,
    ProductSeriesProductViewObject
} from '@/types/apiServerContract';
import ProductTileStock from '@/project/product/product-tile/ProductTileStock.vue';
import authService from '@/core/auth/auth.service';
import serverContextService from '@/core/serverContext.service';
import GTMBasics from '@/project/tracking/gtm/GTM.basics';
import responsiveProductImageService from '@/project/product/image/responsiveProductImage.service';
import { ProductSeriesObject } from '@/project/product/product-details/product-series';
import stockFetchByPostService from '@/project/product/services/stockFetchByPost.service';

interface tableCell {
    tag?: string;
    content?: string;
    img?: string;
    class?: string;
    product?: ProductSeriesProductViewObject;
}

@Component({
    components: { ProductTileStock }
})
export default class ProductDetailsSeriesTable extends Vue {
    @Prop({ required: true, type: String }) mpn!: string;
    @Prop({ required: true, type: Object }) series!: ProductSeriesObject;
    stockData: StockViewObjectExtended[] = [];
    loaded: boolean = false;
    stockLoaded: boolean = false;
    isERPServiceAvailable = true;
    tableData: tableCell[][] | null = null;

    cancellationCallbacks: (() => void)[] = [];

    mounted() {
        this.generateTableData();
        this.getStockData();
    }

    get tableClasses() {
        if (this.series?.dropDowns.length < 2) {
            return 'w-auto';
        }
        return {
            'w-auto': this.series?.dropDowns[1]?.values?.length < 5,
            'min-w-1/3': this.series?.dropDowns[1]?.values?.length < 3,
            'min-w-2/3': this.series?.dropDowns[1]?.values?.length > 3 && this.series?.dropDowns[1]?.values?.length < 5
        };
    }

    generateTableData() {
        if (!this.series?.dropDowns?.length) {
            return;
        }

        let rows: ProductSeriesDropDownViewObject, columns: ProductSeriesDropDownViewObject;

        // If there's only one dropdown (1 dimension),
        // we add another one to make table calculation easier.
        if (this.series.dropDowns.length === 1) {
            rows = this.series.dropDowns[0];
            columns = { displayName: '', facetId: '', values: [{ wid: '', displayValue: '' }] };
        } else {
            rows = this.series.dropDowns[0];
            columns = this.series.dropDowns[1];
        }

        // Make the table data
        const tableRowsData: tableCell[][] = [];

        // Table header first
        const tableHeaderRow: tableCell[] = [];
        tableHeaderRow.push({
            tag: 'th',
            content: this.series?.dropDowns[0].displayName,
            class: 'text-left font-medium'
        });

        columns.values.forEach((colorValue) => {
            const product = this.series?.productSeriesProducts.find(p => {
                return Object.keys(p.facetValues).find(key => p.facetValues[key].displayValue === colorValue.displayValue);
            });

            let img: string | undefined = product?.imageUrl;
            if (img) {
                img = responsiveProductImageService.formatImageUrl(img, responsiveProductImageService.ImageSizePlaceholderValues.W172);
            }
            const cell = {
                tag: 'th',
                content: colorValue.displayValue,
                img,
                class: 'text-left font-medium text-center'
            };
            tableHeaderRow.push(cell);
        });
        tableRowsData.push(tableHeaderRow);

        // Now the table body with the products
        const allCellClasses: string[] = ['px-10 pt-5 pb-10 md:p-10 md:py-7 border-2 border-white'];
        rows.values.forEach(lengthValue => {
            const row: tableCell[] = [{
                tag: 'th',
                content: this.$dictionary.translate(lengthValue.displayValue),
                class: ['text-left font-medium whitespace-no-wrap bg-gray-300', ...allCellClasses].join(' ')
            }];

            columns.values.forEach((colorValue) => {
                const product = this.getProductForSerieType(lengthValue, colorValue);
                const cell = {
                    tag: 'td',
                    product: product,
                    class: [product?.isSelected ? 'font-semi-bold' : '', 'bg-gray-300 whitespace-no-wrap', ...allCellClasses].join(' ')
                };
                row.push(cell);
            });
            tableRowsData.push(row);
        });

        // First col should be selected product. Run through all products and find the selected one and save it for later
        let firstRowIndex: number = 0;
        rows.values.forEach(lengthValue => {
            columns.values.forEach((colorValue, colorIndex) => {
                const product = this.getProductForSerieType(lengthValue, colorValue);
                if (product?.wid === colorValue.wid && lengthValue.wid === product?.wid) {
                    firstRowIndex = colorIndex;
                }
            });
        });

        // Move selected product col as first col, and insert every product after this
        tableRowsData.forEach((row, rowIndex) => {
            const descriptionCell = row.slice(0, 1);
            const cellsToMoveToBack = row.slice(1, firstRowIndex + 1);
            const cellsToMoveToFront = row.slice(firstRowIndex + 1);
            tableRowsData[rowIndex] = [...descriptionCell.concat(cellsToMoveToFront).concat(cellsToMoveToBack)];
        });

        // Save table data
        this.tableData = tableRowsData;
    }

    getProductForSerieType(lengthVariant: ProductSeriesDropDownValueViewObject, colorVariant: ProductSeriesDropDownValueViewObject) {
        const product = this.series?.productSeriesProducts.find(p => {
            if (colorVariant.displayValue === '') {
                // If there is only one variant, there is only one dimension in the table.
                return p.wid === lengthVariant.wid;
            }

            const keys = Object.keys(p.facetValues);
            return (p.facetValues[keys[0]].displayValue === colorVariant?.displayValue ||
                p.facetValues[keys[1]].displayValue === colorVariant?.displayValue) &&
                (p.facetValues[keys[0]].displayValue === lengthVariant?.displayValue ||
                p.facetValues[keys[1]].displayValue === lengthVariant?.displayValue);
        });
        return product;
    }

    resetStockData() {
        this.stockData = [];
    }

    getStockData() {
        this.stockLoaded = false;
        this.resetStockData();
        try {
            if (this.series.productSeriesProducts) {
                this.series.productSeriesProducts.forEach(product => this.getStockItem(product.itemId));
            }
        } catch {
        } finally {
            this.stockLoaded = true;
        }
    }

    async getStockItem(itemId: string) {
        const request = stockFetchByPostService.requestData(itemId);
        this.cancellationCallbacks.push(request.cancel);
        const result = await request.promise;
        if (!this.stockData.find(s => s && s.itemId === itemId)) {
            this.stockData.push(Object.freeze(result));
        }

        this.isERPServiceAvailable = this.isERPServiceAvailable && (result?.isServiceAvailable ?? false);
    }

    getStockByItemId(itemId: string) {
        const stockArray = this.stockData.filter(s => itemId && s.itemId === itemId).map(m => m.stockInformation);
        return stockArray.length ? stockArray[0] : [];
    }

    get isUserLoggedIn(): boolean {
        return authService.isLoggedIn();
    }

    trackClickProductLink(seriesItem, variant) {
        GTMBasics.pushToDataLayer({
            event: 'variant.click',
            variantAttribute: seriesItem.facetDisplayName || seriesItem.displayValue, // English display name TODO: English
            variantValue: variant.facetDisplayName || variant.displayValue || variant.displayName, // English display name TODO: English
            seriesClassificationId: seriesItem.facetId // ID of the product series classification
        });
    }

    productDetailsUrl(productAlternative) {
        const product:{ itemId: string, url?: string, mpn?: string } = {
            itemId: productAlternative.itemId,
            url: productAlternative.productUrl
        };
        return serverContextService.productUrl(product);
    }

    get productDetailsUrlTarget() {
        return serverContextService.externalProductLinkTarget;
    }
}
